import { useState, useEffect, useCallback } from 'react'
import {
  getUsers,
  getUser,
  suspendUser,
  unsuspendUser,
  banUser,
  exportUserData,
  type UserSummary,
  type UserDetail,
  type UserFilters,
} from '../../services/adminApi'
import { UserCard } from '../../components/cards/UserCard'
import { UserDetailDrawer } from './UserDetailDrawer'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'banned', label: 'Banned' },
]

export function Users() {
  const [users, setUsers] = useState<UserSummary[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [filters, setFilters] = useState<UserFilters>({
    status: '',
    search: '',
    page: 1,
    page_size: 20,
  })

  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebouncedValue(searchInput, 300)

  useEffect(() => {
    setFilters((prev) => (prev.search === debouncedSearch ? prev : { ...prev, search: debouncedSearch, page: 1 }))
  }, [debouncedSearch])

  const [selectedUser, setSelectedUser] = useState<UserDetail | null>(null)

  const loadUsers = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getUsers(filters)
      if (response.ok && response.data) {
        setUsers(response.data.items)
        setTotal(response.data.total)
        setPage(response.data.page)
        setTotalPages(response.data.total_pages)
      } else {
        setError(response.error?.message || 'Failed to load users')
      }
    } catch {
      setError('Failed to connect to API')
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  const handleFilterChange = (key: keyof UserFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }))
  }

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }))
  }

  const handleOpenDetail = async (user: UserSummary) => {
    const response = await getUser(user.id)
    if (response.ok && response.data) {
      setSelectedUser(response.data)
    }
  }

  const handleSuspend = async (reason: string, notes?: string) => {
    if (!selectedUser) return
    const response = await suspendUser(selectedUser.id, reason, notes)
    if (response.ok && response.data) {
      setSelectedUser(response.data)
      loadUsers()
    }
  }

  const handleUnsuspend = async (notes?: string) => {
    if (!selectedUser) return
    const response = await unsuspendUser(selectedUser.id, notes)
    if (response.ok && response.data) {
      setSelectedUser(response.data)
      loadUsers()
    }
  }

  const handleBan = async (reason: string) => {
    if (!selectedUser) return
    const response = await banUser(selectedUser.id, reason)
    if (response.ok && response.data) {
      setSelectedUser(response.data)
      loadUsers()
    }
  }

  const handleExport = async () => {
    if (!selectedUser) return
    const response = await exportUserData(selectedUser.id)
    if (response.ok && response.data) {
      // Download as JSON file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `user-export-${selectedUser.id}.json`
      a.click()
      URL.revokeObjectURL(url)
    }
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-gray-900">Users</h2>
          <p className="text-sm text-gray-500">{total} total users</p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="w-64">
          <CustomTextInput
            label=""
            placeholder="Search by name or email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <select
          value={filters.status || ''}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          className="rounded-xl border border-gray-300 px-4 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {statusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 rounded-xl bg-red-50 p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Users Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No users found
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {users.map((user) => (
            <UserCard key={user.id} user={user} onClick={handleOpenDetail} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <CustomButton
            onClick={() => handlePageChange(page - 1)}
            disabled={page <= 1}
            variant="secondary"
          >
            Previous
          </CustomButton>
          <span className="text-sm text-gray-600">
            Page {page} of {totalPages}
          </span>
          <CustomButton
            onClick={() => handlePageChange(page + 1)}
            disabled={page >= totalPages}
            variant="secondary"
          >
            Next
          </CustomButton>
        </div>
      )}

      {/* Detail Drawer */}
      {selectedUser && (
        <UserDetailDrawer
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onSuspend={handleSuspend}
          onUnsuspend={handleUnsuspend}
          onBan={handleBan}
          onExport={handleExport}
          onUserUpdated={(updated) => {
            setSelectedUser(updated)
            loadUsers()
          }}
        />
      )}
    </div>
  )
}
